import React, { useEffect, useMemo, useState } from 'react';
import { CellTypes, Column } from '~/Components/DataTable/DataTable.types';
import { currencyToSymbol } from '~/Constants/Currency.constants';
import { centsToEuros } from '~/Helpers/Currency.helpers';
import { useDateFns, useLanguage } from '~/Hooks';
import { PayoutsStatusType } from '~/Models/DTO/Payouts.model';
import { useRequestPayoutsInvoice } from '~/requests/uscProxyService/useRequestPayoutsList';
import { Button, StatusTag } from '~/UIKit';

type IPayoutsColumnNameType =
  | 'period'
  | 'uploadAt'
  | 'invoiceNumber'
  | 'status'
  | 'amount'
  | 'download';
type IAmountType = {
  amount: number;
  currency: string;
};
type IUploadDateColumn = {
  visible_date: string;
  status: string;
};
type IDownload = {
  id: number;
  status: string;
};

export const usePayoutsColumns = () => {
  const { t } = useLanguage();
  const { formatDate } = useDateFns();
  const [invoiceId, setInvoiceId] = useState(0);
  const [buttonClicked, setButtonClicked] = useState(false);
  const {
    downloadInvoicesFetching,
    downloadInvoicesLoading,
    downloadInvoicesData,
    refetchDownloadInvoices,
  } = useRequestPayoutsInvoice(invoiceId);

  useEffect(() => {
    if (invoiceId === 0) return;
    refetchDownloadInvoices();
  }, [invoiceId]);

  useEffect(() => {
    if (downloadInvoicesLoading || downloadInvoicesFetching) return;
    if (downloadInvoicesData?.url && buttonClicked) {
      window.open(downloadInvoicesData?.url, '_blank', 'noopener, noreferrer');
      setButtonClicked(false);
    }
  }, [downloadInvoicesFetching, downloadInvoicesLoading, buttonClicked]);

  const handleButtonClick = (id: number) => {
    setButtonClicked(true);
    setInvoiceId(id);
  };

  const displayCurrency = (amount: string, currency: string) =>
    `${centsToEuros(amount)} ${currency}`;

  const filterOldDate = (date: string, status: string) => {
    if (status === 'cancelled') return '-';
    return formatDate(date, 'dd.MM.yyyy');
  };

  const downloadButton = (value: number) => (
    <Button variant="outlined" size="medium" onClick={() => handleButtonClick(value)}>
      {t('app.download')}
    </Button>
  );

  const columnMap: {
    [key in IPayoutsColumnNameType]: Column;
  } = useMemo(
    () => ({
      period: {
        label: t('app.payouts.period'),
        propertyIds: 'service_period',
        cellType: CellTypes.String,
        formatter: (value: string) => formatDate(value, 'MMMM yyyy'),
      },
      uploadAt: {
        label: t('app.payouts.uploadDate'),
        propertyIds: ['visible_date', 'status'],
        cellType: CellTypes.String,
        formatter: (value: IUploadDateColumn) => filterOldDate(value.visible_date, value.status),
      },
      invoiceNumber: {
        label: t('app.payouts.reference'),
        propertyIds: 'invoice_number',
        cellType: CellTypes.String,
      },
      status: {
        label: t('app.payouts.status'),
        propertyIds: 'status',
        cellType: CellTypes.String,
        formatter: (value: string) =>
          value && (
            <StatusTag status={PayoutsStatusType[value]} size="medium">
              {value === 'cancelled'
                ? t('app.payouts.status.cancelled')
                : t('app.payouts.status.valid')}
            </StatusTag>
          ),
      },
      amount: {
        label: t('app.payouts.amount'),
        propertyIds: 'payout_amount_gross',
        cellType: CellTypes.Currency,
        formatter: (value: IAmountType) =>
          displayCurrency(value.amount.toString(), currencyToSymbol[value.currency]),
      },
      download: {
        label: '',
        cellType: CellTypes.String,
        propertyIds: ['id', 'status'],
        formatter: (value: IDownload) => value.status !== 'cancelled' && downloadButton(value.id),
      },
    }),
    [t],
  );
  return {
    columnMap,
  };
};
